import React, { useMemo } from "react";
import { Box, Grid, Alert, CircularProgress, useTheme } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import { StatCard, DonutChart } from "../../../../../shared/components/charts";
import { DashboardCard, CohortGuard } from "../../../../../shared/components";
import { useMetricsStats } from "../../../../../shared/hooks";
import type { MetricStatValue, MetricsFilters } from "../../../../../shared/api/types";
import { useStrainActivityStats, useHeartRateZonesDistribution } from "../hooks";

const fallbackStats: Record<string, MetricStatValue> = {
  day_strain: {
    label: "Avg Day Strain",
    value: 0,
    unit: null,
    change: "+00%",
    previous_period_value: 0,
    rag_label: "Good",
    rag_color: "green",
    rag_insight: "Average day strain on a 0-21 scale (higher indicates more load).",
    additional_fields: { max: 21 },
  },
  active_time: {
    label: "Avg Active Time",
    value: 0,
    unit: "min",
    change: "+00%",
    previous_period_value: 0,
    rag_label: "Good",
    rag_color: "green",
    rag_insight:
      "Average active time in minutes derived from time spent in heart rate zones during workouts.",
  },
  calories_burn: {
    label: "Avg Calories Burned",
    value: 0,
    unit: "kcal",
    change: "+00%",
    previous_period_value: 0,
    rag_label: "Good",
    rag_color: "green",
    rag_insight:
      "Average calories burned in kilocalories (kcal), derived from energy in kJ.",
  },
};

interface StrainActivityStatsColumnProps {
  filterParams: MetricsFilters;
  isCentralAdmin: boolean;
  isAnonymised: boolean;
  stacked?: boolean;
}

/**
 * Renders Strain & Activity's three stat cards + the Heart Rate Zones donut for
 * a single filter scope. Safe to render once per comparison group.
 */
export const StrainActivityStatsColumn: React.FC<
  StrainActivityStatsColumnProps
> = ({ filterParams, isCentralAdmin, isAnonymised, stacked = false }) => {
  const theme = useTheme();
  const statsQuery = useStrainActivityStats(filterParams);
  const distributionQuery = useHeartRateZonesDistribution(filterParams);
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ["active_users"] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;

  const statCards = useMemo(() => {
    const stats = statsQuery.data?.data;
    return [
      { metric: stats?.day_strain || fallbackStats.day_strain, icon: BoltIcon },
      { metric: stats?.active_time || fallbackStats.active_time, icon: AccessTimeIcon },
      { metric: stats?.calories_burn || fallbackStats.calories_burn, icon: LocalFireDepartmentIcon },
    ];
  }, [statsQuery.data]);

  const distributionData = useMemo(() => {
    if (!distributionQuery.data?.data?.distribution) return [];
    return distributionQuery.data.data.distribution.map((item) => ({
      label: item.label,
      value: item.value,
      color: item.label.includes("Zone 1-3")
        ? theme.palette.success.main
        : theme.palette.error.main,
    }));
  }, [distributionQuery.data, theme]);

  const isLoading = statsQuery.isLoading || distributionQuery.isLoading;
  const statColSize = stacked ? { xs: 12, sm: 4 } : { xs: 12, sm: 6, md: 4 };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {isLoading
          ? [1, 2, 3].map((i) => (
              <Grid size={statColSize} key={i}>
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              </Grid>
            ))
          : statCards.map((stat, index) => (
              <Grid size={statColSize} key={index}>
                <StatCard metric={stat.metric} icon={stat.icon} />
              </Grid>
            ))}
      </Grid>

      <DashboardCard
        title="Heart Rate Zones"
        subtitle="Distribution of time spent across heart rate intensity zones, reflecting training load and exertion control."
        metricKey="heart_rate"
        expandable={false}
      >
        <CohortGuard
          userCount={activeUserCount}
          bypass={isCentralAdmin || !isAnonymised}
          loading={statsQuery.isLoading}
        >
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 260 }}>
              <CircularProgress />
            </Box>
          ) : distributionData.length > 0 ? (
            <DonutChart data={distributionData} height={260} />
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 260 }}>
              <Alert severity="info">No distribution data available</Alert>
            </Box>
          )}
        </CohortGuard>
      </DashboardCard>
    </Box>
  );
};
